/* Capital 3D — 3D blaster colour preview + colour picker for the Dawn theme.
   Vanilla, no libraries beyond model-viewer itself. Loaded only by
   main-product.liquid and only for products with a custom.colour_3d_model.

   Ported from the WIP theme's product-blaster.js + product-blaster-3d.js, which
   were two files sharing a page. On Dawn they collapse into one, because Dawn
   already owns what the shared file used to do (variant buttons, price, the
   add-to-cart request and cart drawer). What's left, and lives here:
     1. Colour picker: swatch selection, the properties[...] value, validation.
     2. Applying swatch colours to the GLB's materials.
     3. Showing the model instead of the photo, only for variants that need
        colours, and keeping that in step with Dawn's variant changes.
     4. The mobile sticky viewer.

   Everything is wired by EVENT DELEGATION on `document`, or by Dawn's pubsub,
   never by listeners bound to elements queried once at load. Dawn re-fetches the
   product page on every variant change and reconciles parts of it, and the
   theme editor innerHTML-swaps a whole section on every setting change — a
   listener bound to the old nodes would go dead. Delegated ones don't care
   which node fired. */
(function () {
  'use strict';

  var ROLES = ['primary', 'secondary', 'accent'];

  /* role -> CSS colour string ('' = no selection). The model may not have
     finished loading when a swatch is clicked, and a theme-editor reload throws
     the viewer away and rebuilds it; either way the 'load' handler replays
     whatever is in here. */
  var pending = {};

  /* Shown for these variants only. Same rule as the server-side computation in
     product-colour-picker-3d.liquid and product-thumbnail.liquid — keep all
     three in step. */
  function variantNeedsColour(title) {
    title = (title || '').toLowerCase();
    return title.indexOf('printed') !== -1 || title.indexOf('diy') !== -1 || title.indexOf('assembled') !== -1;
  }

  /* The hardware box and spring are about the hardware build: hidden for
     "Printed Parts Only", shown for everything else including DIY/Assembled
     (both still include hardware despite also saying "printed"). */
  function variantHasHardware(title) {
    title = (title || '').toLowerCase();
    return title.indexOf('hardware') !== -1 || title.indexOf('diy') !== -1 || title.indexOf('assembled') !== -1;
  }
  function variantIsPrintedOnly(title) {
    title = (title || '').toLowerCase();
    return title.indexOf('printed') !== -1 && !variantHasHardware(title);
  }

  function viewers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-c3d-model]'));
  }

  /* model.materials is a live accessor on the loaded scene; copy it so the loops
     below don't depend on what kind of collection it is. */
  function materialsOf(mv) {
    if (!mv || !mv.model || !mv.model.materials) return [];
    return Array.prototype.slice.call(mv.model.materials);
  }

  /* ── Materials ──
     Colourable materials are identified by name, by convention, so a product
     needs no per-model mapping — name the material in Blender and it just works.
     Leading punctuation is stripped and the match is a case-insensitive prefix,
     so `Primary`, `Primary_Shell`, `primary.001` and `Primary Body` all resolve
     to the same role, and several materials can share one. Anything else
     (springs, screws, decals) returns null and is never touched. */
  function roleOfMaterial(name) {
    var n = (name || '').replace(/^[^a-z0-9]+/i, '').toLowerCase();
    for (var i = 0; i < ROLES.length; i++) {
      if (n.indexOf(ROLES[i]) === 0) return ROLES[i];
    }
    return null;
  }

  /* Remember what the model was authored as, before anything is applied, so "no
     colour picked" can put a part back exactly how the modeller made it. Stashed
     on the material itself — model-viewer hands back the same instances for the
     life of a load and discards them wholesale when a new model loads. */
  function captureOriginals(mv) {
    materialsOf(mv).forEach(function (m) {
      if (m.c3dOriginalColour) return;
      m.c3dOriginalColour = m.pbrMetallicRoughness.baseColorFactor.slice();
    });
  }

  /* setBaseColorFactor accepts a CSS colour string and does the sRGB -> linear
     conversion itself, so the swatch's own colour value goes straight in. No
     selection restores the authored colour rather than forcing white (which
     made the page load as a blank white blaster). Applied to every viewer at
     once so the big one and the mobile mirror can't drift apart. */
  function applyModelColour(role, colour) {
    pending[role] = colour || '';
    viewers().forEach(function (mv) {
      materialsOf(mv).forEach(function (m) {
        if (roleOfMaterial(m.name) !== role) return;
        m.pbrMetallicRoughness.setBaseColorFactor(colour || m.c3dOriginalColour || '#ffffff');
      });
    });
  }

  /* Printed PLA is matte; a model left on default glTF values reads as glossy
     injection-moulded plastic. Set once per load, not per click, so the surface
     doesn't visibly change when a colour is picked. Blank settings (NaN) leave
     the model's authored values alone. */
  function applyFinish(mv) {
    var rough = parseFloat(mv.getAttribute('data-roughness'));
    var metal = parseFloat(mv.getAttribute('data-metallic'));
    if (isNaN(rough) && isNaN(metal)) return;
    materialsOf(mv).forEach(function (m) {
      if (!roleOfMaterial(m.name)) return;
      if (!isNaN(rough)) m.pbrMetallicRoughness.setRoughnessFactor(rough);
      if (!isNaN(metal)) m.pbrMetallicRoughness.setMetallicFactor(metal);
    });
  }

  function swatchColour(btn) {
    return getComputedStyle(btn).getPropertyValue('--c3d-swatch-colour').trim();
  }

  /* Rebuild `pending` from whatever the swatches currently show rather than
     trusting what we last recorded: after a theme-editor reload the picker comes
     back with nothing selected while old values would still be in memory, and
     replaying them would paint colours the UI says aren't chosen. */
  function syncPendingFromDom() {
    var wrap = document.querySelector('[data-c3d-colour-options]');
    ROLES.forEach(function (role) {
      var active = wrap && wrap.querySelector('[data-c3d-swatch][data-role="' + role + '"].is-active');
      pending[role] = active ? swatchColour(active) : '';
    });
  }

  function replayPending() {
    ROLES.forEach(function (role) { applyModelColour(role, pending[role]); });
  }

  /* Safety net for the Accent role. Whether a third role renders at all is a
     merchant flag because Liquid can't see inside a GLB that hasn't loaded. If
     the flag and the model disagree — ticked, but no Accent* material — the
     customer would be told to pick a colour that changes nothing and be blocked
     at add-to-cart on it. Drop the role and clear its required flag. The
     opposite mismatch is left alone: an Accent* material with the flag off means
     the merchant deliberately isn't offering it. */
  function auditAccentRole(mv) {
    var roleEl = document.querySelector('[data-c3d-colour-role="accent"]');
    if (!roleEl) return;
    var mats = materialsOf(mv);
    if (!mats.length) return;
    for (var i = 0; i < mats.length; i++) {
      if (roleOfMaterial(mats[i].name) === 'accent') return;
    }
    roleEl.classList.add('c3d-cp__role--hidden');
    roleEl.setAttribute('data-required', 'false');
    var input = document.querySelector('[data-c3d-colour-input][data-role="accent"]');
    if (input) {
      input.value = '';
      input.disabled = true;
      input.setAttribute('data-required', 'false');
    }
  }

  /* A model that won't load — corrupt file, no WebGL, blocked context — must not
     leave the customer staring at an empty square. Remove every viewer and put
     the real photo back; the picker keeps working and still records the choice,
     there's just nothing to preview it on. */
  function fallBackToPhotos() {
    viewers().forEach(function (mv) {
      if (mv.parentNode) mv.parentNode.removeChild(mv);
    });
    document.querySelectorAll('[data-c3d-3d-stage]').forEach(function (stage) { stage.hidden = true; });
    document.querySelectorAll('[data-c3d-real-media]').forEach(function (el) { el.style.visibility = ''; });
    colourNeeded = false;
    refreshFloat();
  }

  function bindViewer(mv) {
    if (mv.c3dBound) return;
    mv.c3dBound = true;
    mv.addEventListener('load', function () {
      /* Must run before anything writes to the materials, or the "original" we
         remember is already something we set. */
      captureOriginals(mv);
      applyFinish(mv);
      replayPending();
      /* Only the main viewer drives the picker — the mobile mirror is the same
         model and would just repeat the verdict. */
      if (!mv.hasAttribute('data-c3d-float-model')) auditAccentRole(mv);
    });
    mv.addEventListener('error', fallBackToPhotos);
  }

  /* ── What's on screen for the current variant ──
     Server-side Liquid has already rendered the right state for the variant the
     page loaded with, so nothing here runs at startup. This only reacts to
     variant changes the customer makes. Dawn announces each one on its own
     pubsub after its AJAX swap completes (product-info.js), so we don't need a
     signal of our own. */
  var colourNeeded = false;

  /* Moving back to a variant that has no printed parts (Hardware Kit) must leave no
     colours behind. The picker is only hidden, and a hidden input is still part of the
     form, so without this the colours picked earlier would still go to the cart and
     order. Blank property values are dropped by Shopify, so emptying the inputs is
     enough to keep them off the line item. Also resets the swatches and the model so
     nothing looks chosen when they come back to a printed variant. */
  function clearColourChoices() {
    document.querySelectorAll('[data-c3d-colour-input]').forEach(function (input) { input.value = ''; });
    document.querySelectorAll('[data-c3d-swatch].is-active').forEach(function (b) { b.classList.remove('is-active'); });
    ROLES.forEach(function (role) { applyModelColour(role, ''); });
  }

  /* Emptying the values isn't enough on its own: Shopify keeps a blank line-item
     property, so the cart still listed "Primary Colour", "Secondary Colour" … with
     nothing after them on a Hardware Kit. A DISABLED control isn't submitted at all,
     so the properties vanish from the request entirely. Enabled again for printed
     variants. The Accent input also stays off if its role was dropped (the model has
     no Accent* material), otherwise a blank "Accent Colour" would be sent. */
  function syncColourInputs(needed) {
    document.querySelectorAll('[data-c3d-colour-input]').forEach(function (input) {
      var roleEl = input.closest('[data-c3d-colour-role]');
      var roleDropped = !!roleEl && roleEl.classList.contains('c3d-cp__role--hidden');
      input.disabled = !needed || roleDropped;
    });
  }

  function setVariantState(title) {
    var needed = variantNeedsColour(title);
    colourNeeded = needed;

    document.querySelectorAll('[data-c3d-colour-options]').forEach(function (wrap) {
      wrap.classList.toggle('c3d-cp--hidden', !needed);
      if (!needed) {
        var err = wrap.querySelector('[data-c3d-colour-error]');
        if (err) err.hidden = true;
      }
      /* The note field is hidden along with the picker. A hidden control is still
         part of the form and would still be sent, so switch it off (disabled
         controls aren't submitted) rather than delete what the customer typed —
         it comes back if they switch to a printed variant again. */
      var note = wrap.querySelector('textarea[name="properties[Note]"]');
      if (note) note.disabled = !needed;
    });
    if (!needed) clearColourChoices();
    syncColourInputs(needed);

    /* Real photo and 3D stage swap places. visibility (not display) on the photo
       keeps the slide's box, so the gallery doesn't jump. */
    document.querySelectorAll('[data-c3d-3d-stage]').forEach(function (stage) {
      stage.hidden = !needed;
      var container = stage.closest('.product-media-container');
      var real = container ? container.querySelector('[data-c3d-real-media]') : null;
      if (real) real.style.visibility = needed ? 'hidden' : '';
    });

    var printedOnly = variantIsPrintedOnly(title);
    document.querySelectorAll('[data-c3d-extra-options]').forEach(function (wrap) {
      wrap.classList.toggle('c3d-extra--hidden', printedOnly);
      if (printedOnly) {
        var extraErr = wrap.querySelector('[data-c3d-extra-error]');
        if (extraErr) extraErr.hidden = true;
      }
    });

    /* Showing or hiding the picker changes where its bottom edge is, so the
       "scrolled past the colours" test has to be redone straight away. */
    refreshFloat();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      if (variant) setVariantState(variant.title);
    });
  }

  /* ── Colour picker ──
     Click a swatch to pick it; click the SAME active swatch again to deselect
     back to no selection.

     The captured line-item property VALUE is just the colour name, e.g. "Blue".
     The role is in the property KEY (properties[Primary Colour]), and the hex is
     only used on screen to paint the model — it is deliberately NOT sent to the
     cart/order. (It used to be "Primary: Blue (#1E5FBF)"; changed on request.) */
  function showModelIfPhotoActive() {
    var stage = document.querySelector('[data-c3d-3d-stage]');
    var slide = stage && stage.closest('[data-media-id]');
    var gallery = document.querySelector('media-gallery');
    if (!slide || !gallery || typeof gallery.setActiveMedia !== 'function') return;
    if (slide.classList.contains('is-active')) return;
    /* Only when that slide is actually on screen. setActiveMedia scrolls the page
       up to the slide if it's above the viewport, which would yank the customer
       away from the swatches they're clicking — and in that case the sticky
       viewer is already showing them the model. */
    var r = slide.getBoundingClientRect();
    if (r.bottom <= 0 || r.top >= window.innerHeight) return;
    gallery.setActiveMedia(slide.getAttribute('data-media-id'), false);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-c3d-swatch]');
    if (!btn) return;

    var wrap = btn.closest('[data-c3d-colour-options]');
    if (!wrap) return;

    var role = btn.getAttribute('data-role');
    var wasActive = btn.classList.contains('is-active');
    var input = wrap.querySelector('[data-c3d-colour-input][data-role="' + role + '"]');

    wrap.querySelectorAll('[data-c3d-swatch][data-role="' + role + '"]').forEach(function (b) {
      b.classList.remove('is-active');
    });

    if (wasActive) {
      if (input) input.value = '';
      applyModelColour(role, '');
    } else {
      btn.classList.add('is-active');
      /* Just the colour name, e.g. "Blue". The role is already in the property's own
         name (properties[Primary Colour]), and the hex is only needed on screen to
         paint the model, not on the order. */
      var name = btn.getAttribute('data-name');
      var hex = swatchColour(btn);
      if (input) input.value = name;
      applyModelColour(role, hex);
      var err = wrap.querySelector('[data-c3d-colour-error]');
      if (err) err.hidden = true;
      showModelIfPhotoActive();
    }
  });

  /* ── Add to cart ──
     Dawn's product-form.js takes the submit from here: it fetches the cart add
     and opens the cart drawer. All we do is stop it when something required is
     missing. Registered on the CAPTURE phase of `document`, so it always runs
     before Dawn's bubble-phase listener on the form regardless of which script
     tag happened to load first.

     The form is found by reading its id ATTRIBUTE, never `form.id`: the form
     contains <input name="id"> (Shopify's variant id), and a form control named
     "id" shadows the form's own .id property — `form.id` hands back that input
     element, so a string comparison on it can never match (and calling string
     methods on it throws, silently killing the whole listener).

     Dawn's form is `novalidate`, so native `required` on the spring select
     doesn't stop the submit either — that is checked here too. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    var formId = form && form.getAttribute && form.getAttribute('id');
    if (!formId || formId.indexOf('product-form-') !== 0) return;
    /* Dawn also renders a second, installment form on this page — leave it. */
    if (formId.indexOf('product-form-installment-') === 0) return;

    var blocked = false;
    var firstProblem = null;

    var colourWrap = document.querySelector('[data-c3d-colour-options]');

    /* An empty note would be sent as a blank "Note" line on the order. Leave it out
       of the request unless there is something in it (and the picker is showing).
       This runs before Dawn builds the request, so a disabled field is skipped; typing
       in it turns it back on (see the input listener below). */
    var noteEl = document.querySelector('textarea[name="properties[Note]"]');
    if (noteEl) {
      var pickerShowing = !!colourWrap && !colourWrap.classList.contains('c3d-cp--hidden');
      noteEl.disabled = !pickerShowing || !noteEl.value.trim();
    }
    if (colourWrap && !colourWrap.classList.contains('c3d-cp--hidden')) {
      var missing = false;
      colourWrap.querySelectorAll('[data-c3d-colour-role]').forEach(function (roleEl) {
        if (roleEl.getAttribute('data-required') !== 'true') return;
        if (roleEl.classList.contains('c3d-cp__role--hidden')) return;
        var role = roleEl.getAttribute('data-c3d-colour-role');
        var input = colourWrap.querySelector('[data-c3d-colour-input][data-role="' + role + '"]');
        if (!input || !input.value) missing = true;
      });
      if (missing) {
        var err = colourWrap.querySelector('[data-c3d-colour-error]');
        if (err) {
          err.hidden = false;
          firstProblem = firstProblem || err;
        }
        blocked = true;
      }
    }

    var extraWrap = document.querySelector('[data-c3d-extra-options]');
    var spring = extraWrap && extraWrap.querySelector('[data-c3d-spring-select]');
    if (spring && !extraWrap.classList.contains('c3d-extra--hidden') && !spring.value) {
      var extraErr = extraWrap.querySelector('[data-c3d-extra-error]');
      if (extraErr) {
        extraErr.hidden = false;
        firstProblem = firstProblem || extraErr;
      }
      blocked = true;
    }

    if (blocked) {
      e.preventDefault();
      e.stopPropagation();
      if (firstProblem) firstProblem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, true);

  document.addEventListener('input', function (e) {
    if (!e.target.matches || !e.target.matches('textarea[name="properties[Note]"]')) return;
    var wrap = e.target.closest('[data-c3d-colour-options]');
    if (wrap && !wrap.classList.contains('c3d-cp--hidden')) e.target.disabled = false;
  });

  document.addEventListener('change', function (e) {
    if (!e.target.matches || !e.target.matches('[data-c3d-spring-select]')) return;
    var err = e.target.parentNode.querySelector('[data-c3d-extra-error]');
    if (err && e.target.value) err.hidden = true;
  });

  /* ── Mobile sticky viewer ──
     The bar's second viewer holds its first frame back (reveal="manual") so it
     isn't rendering a scene nobody can see. Three independent conditions decide
     whether the bar shows, tracked separately because any can change without the
     others: has the gallery's 3D stage scrolled above the viewport, does this
     variant need colours, and is the last swatch still below the bar. */
  var stageScrolledPast = false;
  var pastPicker = false;
  var floatRevealed = false;

  function refreshFloat() {
    var bar = document.querySelector('[data-c3d-3d-float]');
    if (!bar) return;
    bar.classList.toggle('is-visible', stageScrolledPast && colourNeeded && !pastPicker);
    if (bar.classList.contains('is-visible') && !floatRevealed) {
      var mv = bar.querySelector('[data-c3d-float-model]');
      /* The custom element may not be upgraded yet — try again on the next call. */
      if (mv && typeof mv.dismissPoster === 'function') {
        mv.dismissPoster();
        floatRevealed = true;
      }
    }
  }

  /* Dawn's sticky header can be any height and may slide away on scroll, so the
     bar tracks its bottom edge instead of assuming a number. */
  function updateFloatOffset() {
    var bar = document.querySelector('[data-c3d-3d-float]');
    if (!bar) return;
    var header = document.querySelector('sticky-header') || document.querySelector('.section-header');
    var top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    bar.style.setProperty('--c3d-3d-float-top', top + 'px');
  }

  /* Once the last swatch has passed behind the bar there is nothing left to
     preview, so get out of the way instead of covering a third of the screen
     over the description. Scrolling back up to the picker brings it straight
     back. A scroll listener rather than an IntersectionObserver because the
     trigger line is the bar's own bottom edge, which moves with the header and
     the viewport height. One rect read per frame, rAF-throttled. */
  var frame = null;
  function measure() {
    frame = null;
    var bar = document.querySelector('[data-c3d-3d-float]');
    var picker = document.querySelector('[data-c3d-colour-options]');
    if (!bar) return;
    updateFloatOffset();
    if (picker && !picker.classList.contains('c3d-cp--hidden')) {
      /* The bar keeps its box when hidden (opacity only, never display), so this
         measurement can't oscillate between shown and hidden. */
      pastPicker = picker.getBoundingClientRect().bottom <= bar.getBoundingClientRect().bottom;
    } else {
      pastPicker = false;
    }
    refreshFloat();
  }
  function queueMeasure() {
    if (frame) return;
    frame = window.requestAnimationFrame ? requestAnimationFrame(measure) : (measure(), null);
  }
  window.addEventListener('scroll', queueMeasure, { passive: true });
  window.addEventListener('resize', queueMeasure, { passive: true });

  /* The stage is the thing that scrolls away: it is the featured slide's overlay,
     which keeps its place across Dawn's variant swaps. Observed (rather than a
     sentinel) so the trigger is "the model is gone", not "the whole gallery is" —
     Dawn's stacked layout puts every photo in the gallery. */
  var stageObserver = null;
  function observeStage() {
    if (!('IntersectionObserver' in window)) return;
    if (stageObserver) stageObserver.disconnect();
    var stage = document.querySelector('[data-c3d-3d-stage]');
    if (!stage) return;
    stageObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        /* "Above the viewport" (scrolled past), not "below / not yet reached". */
        stageScrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        refreshFloat();
      });
    }, { threshold: 0 });
    stageObserver.observe(stage);
  }

  /* The bar's mini viewer is the point of it being 3D — you turn the model to
     see the part you're colouring. Keep that drag local to the viewer. */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-c3d-float-model]')) e.stopPropagation();
  }, true);

  /* Initial sync, and again after any section reload — the theme editor replaces
     the markup wholesale, so the viewers and swatches are fresh objects that need
     rebinding and re-reading. */
  function sync() {
    viewers().forEach(bindViewer);
    syncPendingFromDom();
    replayPending();
    /* The stage's own hidden state was rendered server-side from the selected
       variant, so it is the one place that already knows. */
    var stage = document.querySelector('[data-c3d-3d-stage]');
    colourNeeded = !!stage && !stage.hidden;
    floatRevealed = false;
    observeStage();
    queueMeasure();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
  document.addEventListener('shopify:section:load', sync);
})();
